import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Tooltip,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Container,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { CirclePicker } from "react-color";
import toast from "../../utils/toast.jsx";
import HelpHint from "../parts/helpHint";
import { useNavigate } from "react-router-dom";
import { TiersApi } from "../../services/api/tiers";
import TierDropdown from "../parts/tierDropdown";

function TierAdd(props) {
  const { token } = props;
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [name, setName] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [notes, setNotes] = useState("no note");
  const [formTier, setFormTier] = useState("0");
  const [loading, setLoading] = useState(false);
  const [typeStep, setTypeStep] = useState(0);
  const isMounted = useRef(true);
  const typeWriterTimeout = useRef(null);

  const colors = [
    "#f44336",
    "#e91e63",
    "#ff91c6",
    "#cc99ff",
    "#9c27b0",
    "#673ab7",
    "#3f51b5",
    "#2196f3",
    "#03a9f4",
    "#00bcd4",
    "#5fe8de",
    "#009688",
    "#4caf50",
    "#8bc34a",
    "#cddc39",
    "#ffeb3b",
    "#ffc107",
    "#ff9800",
    "#ff5722",
    "#795548",
    "#607d8b",
  ];

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (typeWriterTimeout.current) {
        clearTimeout(typeWriterTimeout.current);
      }
    };
  }, []);

  const submitForm = async (ev) => {
    ev.preventDefault();
    if (loading) return;

    if (name.trim() === "") {
      toast.warn("You can't have an empty tier name.", { autoClose: 5000 });
      return;
    }
    setLoading(true);

    try {
      let authToken = token;
      if (typeof authToken === 'function') {
        authToken = await authToken();
      }
      if (!authToken || typeof authToken !== 'string' || authToken === 'undefined') {
        toast.error('No valid token found for tier add API call.');
        setLoading(false);
        return;
      }

      const response = await TiersApi.createTier(authToken, {
        tierName: name,
        color: selectedColor || "#FFFFFF",
        parentTierId: formTier || "0",
        notes: notes,
      });

      if (response.success) {
        toast.success("Tier created successfully!");
        navigate("/tiers");
      } else {
        toast.error(`Error: ${response.message || "Failed to create tier."}`);
      }
    } catch (err) {
      toast.error("An error occurred while creating the tier.");
      console.error(err);
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleTierChange = (color, id, name) => {
    setFormTier(id);
  };

  const handleChangeComplete = (color) => {
    setSelectedColor(color.hex);
  };

  const typeWriter = (text, setter, i = 0) => {
    if (i < text.length) {
      setter(text.substring(0, i + 1));
      typeWriterTimeout.current = setTimeout(() => typeWriter(text, setter, i + 1), 20);
    }
  };

  return (
    <>
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 1, sm: 2, md: 3 },
          width: isMobile ? "95vw" : "100%"
        }}
      >
        <Paper 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            borderRadius: { xs: 1, sm: 2 }, 
            boxShadow: { xs: 1, sm: 2, md: 3 },
            mx: { xs: 0, sm: 1, md: 2 }
          }} 
          id="tour-01"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography 
              variant={isMobile ? "h5" : "h4"}
            gutterBottom 
            sx={{ 
              mb: { xs: 2, sm: 3 }, 
              fontWeight: 600, 
              color: "primary.main",
              textAlign: { xs: 'center', sm: 'left' }
            }}
          >
            Add Tier
          </Typography>
            <HelpHint
              title="Add a Tier"
              hints={[
                "Give the tier a name your subscribers will recognize",
                "Pick a color to keep tiers visually distinct",
                "Optionally nest it under a parent tier",
              ]}
            />
          </Box>
          <Box component="form" onSubmit={submitForm}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={8} id="tour-02">
                <TextField
                  label="Tier Name"
                  variant="outlined"
                  fullWidth
                  required
                  inputProps={{ 'data-testid': 'tier-name-input' }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ 
                    mb: 1,
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    Linked Tier
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip 
                      title="Distributions to the linked tier will include your added tier." 
                      arrow 
                      placement="top"
                      PopperProps={{
                        modifiers: [
                          {
                            name: 'flip',
                            enabled: false,
                          },
                        ],
                      }}
                    >
                      <span>
                        <TierDropdown
                          action={handleTierChange}
                          token={token}
                          defaultValue={"0"}
                          label="Includes"
                        />
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} id="tour-03">
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    color: selectedColor || "text.primary", 
                    fontWeight: 500,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  Select a Color
                </Typography>
                <Box sx={{ 
                  mt: 0.5, 
                  display: 'flex', 
                  justifyContent: { xs: 'center', sm: 'flex' },
                  overflowX: 'hidden',
                  pb: 2,
                  minHeight: { xs: 64, sm: 72 }, // Ensures enough height for color picker
                  alignItems: 'center'
                }}>
                  <CirclePicker
                    color={selectedColor}
                    colors={colors}
                    width={isMobile ? "280px" : "100%"}
                    onChangeComplete={handleChangeComplete}
                    circleSpacing={isMobile ? 8 : 10}
                    circleSize={isMobile ? 24 : 28}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} id="tour-04">
                <TextField
                  label="Notes"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={isMobile ? 3 : 4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add a Note here... Not required"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ pt: { xs: 2, sm: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: { xs: 'center', sm: 'flex-start' } 
                }}>
                  <Button
                    id="submitButton"
                    type="submit"
                    data-testid="create-tier-button"
                    variant="contained"
                    size={isMobile ? "medium" : "large"}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : null}
                    sx={{ 
                      px: { xs: 3, sm: 4 }, 
                      py: { xs: 1, sm: 1.5 },
                      minWidth: { xs: '200px', sm: 'auto' },
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {loading ? "Creating Tier..." : "Create Tier"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </>
  );
}

export default TierAdd;
